import React, { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Fade,
  LinearProgress
} from "@mui/material";
import {
  SupportAgent as SupportIcon,
  Star as StarIcon,
  WarningAmber as WarningIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon
} from "@mui/icons-material";
import { useCollection } from "../hooks/useCollection";
import { Order } from "../interfaces/Order";
import { formatCOP } from "../components/format";
import { differenceInMinutes } from "date-fns";

// Utility to safely parse dates
const toDate = (dateValue: any): Date | null => {
  if (!dateValue) return null;
  let dateObj: Date;
  if (typeof dateValue.toDate === "function") {
    dateObj = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    dateObj = dateValue;
  } else if (typeof dateValue === "number") {
    dateObj = new Date(dateValue);
  } else if (dateValue && typeof dateValue === "object" && (dateValue.seconds !== undefined || dateValue._seconds !== undefined)) {
    const s = dateValue.seconds !== undefined ? dateValue.seconds : dateValue._seconds;
    dateObj = new Date(s * 1000);
  } else {
    dateObj = new Date(dateValue);
  }
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

interface CustomerStats {
  customerName: string;
  totalOrders: number;
  totalSpent: number;
  totalWaitMinutes: number;
  delayedCount: number; // > 45 mins total wait
  averageWaitTime: number;
  phone?: string;
}

export default function CustomerMetricsPage() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { data: orders = [] } = useCollection<Order>(restaurantId!, "orderssuccess");

  const [dateRange, setDateRange] = useState<"today" | "week" | "month" | "all">("month");

  const DELAYED_THRESHOLD = 45; // 45 minutes for a complete order is considered delayed

  // Filter orders by date range
  const filteredOrders = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let startDate: Date | null = startOfToday;
    if (dateRange === "week") {
      startDate = new Date(startOfToday);
      startDate.setDate(startDate.getDate() - 7);
    } else if (dateRange === "month") {
      startDate = new Date(startOfToday.getFullYear(), startOfToday.getMonth(), 1);
    } else if (dateRange === "all") {
      startDate = null;
    }

    return orders.filter(order => {
      // Only care about completed orders
      if (order.state === "cancelada") return false;

      const orderDate = toDate(order.date || order.dateSuccess);
      if (!orderDate) return false;
      if (startDate && orderDate < startDate) return false;
      return true;
    });
  }, [orders, dateRange]);

  // Aggregate metrics
  const customerStats = useMemo(() => {
    const statsMap: Record<string, CustomerStats> = {};

    filteredOrders.forEach(order => {
      // Intentar obtener el correo de createdByName o del nombre.
      let emailOrName = order.createdByName || order.customerName || order.name || "";
      
      // Si el pedido fue tomado por un empleado (mesero, admin, cajero), order.createdByRole existe.
      // Los pedidos de la App NO tienen createdByRole (o es undefined/null/"cliente").
      const isFromEmployee = order.createdByRole === "mesero" || order.createdByRole === "cajero" || order.createdByRole === "admin" || order.createdByRole === "mesera";

      if (isFromEmployee) {
        return; // Exclusivo para App/Web: Ignoramos pedidos tomados por empleados
      }

      // Si queremos estrictamente que tenga un "@" (como pidió el usuario "solo coge los correos"):
      if (!emailOrName.includes("@")) {
        return;
      }

      const keyName = emailOrName.trim().toLowerCase();
      const displayKey = emailOrName.trim().toLowerCase();

      if (!statsMap[keyName]) {
        statsMap[keyName] = {
          customerName: displayKey,
          totalOrders: 0,
          totalSpent: 0,
          totalWaitMinutes: 0,
          delayedCount: 0,
          averageWaitTime: 0,
        };
      }

      const stats = statsMap[keyName];
      stats.totalOrders += 1;
      
      const orderTotal = Number(order.total || (order as any).totalPrice || 0);
      stats.totalSpent += orderTotal;

      const start = toDate(order.date);
      const end = toDate(order.dateSuccess);
      
      if (start && end) {
        const durationMins = differenceInMinutes(end, start);
        if (durationMins > 0 && durationMins < 300) { // filter out anomalous data (e.g. forgot to close order for 5 hours)
          stats.totalWaitMinutes += durationMins;
          if (durationMins > DELAYED_THRESHOLD) {
            stats.delayedCount += 1;
          }
        }
      }
    });

    // Calculate averages and convert to array
    return Object.values(statsMap)
      .map(stats => {
        stats.averageWaitTime = stats.totalOrders > 0 
          ? Math.round(stats.totalWaitMinutes / stats.totalOrders) 
          : 0;
        return stats;
      })
      .sort((a, b) => b.totalSpent - a.totalSpent); // Sort by spend descending

  }, [filteredOrders]);

  // Highlights
  const topCustomer = useMemo(() => {
    if (customerStats.length === 0) return null;
    return customerStats[0]; // Already sorted by totalSpent
  }, [customerStats]);

  const needsAttention = useMemo(() => {
    if (customerStats.length === 0) return null;
    // Customer with the most delayed orders, but minimum 1 delayed order
    const sortedByDelay = [...customerStats].sort((a, b) => b.delayedCount - a.delayedCount);
    if (sortedByDelay[0].delayedCount > 0) {
      return sortedByDelay[0];
    }
    return null;
  }, [customerStats]);

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pb: 6, px: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={4} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1.5, bgcolor: "secondary.main", borderRadius: 2, display: "flex", color: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
            <SupportIcon />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: "-0.02em", color: "text.primary" }}>
              Métricas de Clientes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Fidelidad, compras y experiencia de entrega
            </Typography>
          </Box>
        </Stack>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Período</InputLabel>
          <Select
            value={dateRange}
            label="Período"
            onChange={(e) => setDateRange(e.target.value as any)}
            sx={{ borderRadius: 2, fontWeight: 700 }}
          >
            <MenuItem value="today">Hoy</MenuItem>
            <MenuItem value="week">Últimos 7 días</MenuItem>
            <MenuItem value="month">Este Mes</MenuItem>
            <MenuItem value="all">Siempre</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Fade in timeout={800}>
        <Grid container spacing={3} mb={5}>
          {/* Top Customer Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 4, border: "1px solid #fef08a", bgcolor: "#fefce8", boxShadow: "none", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{ bgcolor: "#eab308", width: 56, height: 56 }}>
                    <StarIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="warning.dark" fontWeight={800} textTransform="uppercase" letterSpacing={1}>
                      Mejor Cliente 👑
                    </Typography>
                    {topCustomer ? (
                      <>
                        <Typography variant="h5" fontWeight={900} color="text.primary" mt={0.5}>
                          {topCustomer.customerName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          Ha comprado <strong>{topCustomer.totalOrders}</strong> veces en este período.
                          <br/>
                          Total invertido: <strong style={{ color: '#854d0e' }}>{formatCOP(topCustomer.totalSpent)}</strong>
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        No hay suficientes datos en este período.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Needs Attention Card */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ borderRadius: 4, border: "1px solid #fee2e2", bgcolor: "#fef2f2", boxShadow: "none", height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{ bgcolor: "#ef4444", width: 56, height: 56 }}>
                    <WarningIcon fontSize="large" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="error.main" fontWeight={800} textTransform="uppercase" letterSpacing={1}>
                      Atención al Cliente
                    </Typography>
                    {needsAttention ? (
                      <>
                        <Typography variant="h5" fontWeight={900} color="text.primary" mt={0.5}>
                          {needsAttention.customerName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          Tiene <strong>{needsAttention.delayedCount}</strong> pedidos demorados ({">"} {DELAYED_THRESHOLD} min).
                          <br/>
                          Posible candidato para un bono o disculpa.
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        ¡Excelente! No hay clientes con múltiples demoras.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Fade>

      {/* Main Table */}
      <Typography variant="h6" fontWeight={800} mb={2}>
        Historial de Clientes
      </Typography>
      
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, maxHeight: 600 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Cliente (Correo / Nombre)</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Pedidos</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Total Comprado</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Espera Promedio</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800, bgcolor: "#f8fafc" }}>Experiencia</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customerStats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No hay datos para el período seleccionado.
                </TableCell>
              </TableRow>
            ) : (
              customerStats.map((stat, index) => {
                const total = Math.max(1, stat.totalOrders);
                const delayedPercent = (stat.delayedCount / total) * 100;
                
                return (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{stat.customerName}</TableCell>
                    <TableCell align="center">
                      <Chip label={stat.totalOrders} size="small" sx={{ fontWeight: 800, bgcolor: "#f1f5f9" }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={800} color="primary.main">
                        {formatCOP(stat.totalSpent)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                        <TimeIcon fontSize="small" sx={{ color: stat.averageWaitTime > DELAYED_THRESHOLD ? "error.main" : "text.secondary" }} />
                        <Typography variant="body2" fontWeight={700} color={stat.averageWaitTime > DELAYED_THRESHOLD ? "error.main" : "text.primary"}>
                          {stat.averageWaitTime} min
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Demoras: {stat.delayedCount}
                          </Typography>
                          <Typography variant="caption" color={delayedPercent > 30 ? "error.main" : "success.main"} fontWeight={700}>
                            {Math.round(100 - delayedPercent)}% Bueno
                          </Typography>
                        </Stack>
                        <LinearProgress 
                          variant="determinate" 
                          value={100 - delayedPercent} 
                          color={delayedPercent > 30 ? "error" : "success"}
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  );
}
